import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LOOutcomeModalCst from "@components/LOOutcomeModalCst";
import PathWizard from "../Path/PathWizard";
import { useSubmitPathV2 } from "../Path/hooks/useSubmitPathV2";
import { useSaveDraft } from "../hooks/useSaveDraft";
import { usePromoteDraft } from "../hooks/usePromoteDraft";
import {
  DraftErrorInfo,
  extractPromoteError,
} from "../hooks/parsePromoteErrors";
import DraftPromoteErrorDialog from "../components/DraftPromoteErrorDialog";
import { EMPTY_PATH_DATA, PathWizardData } from "../Path/types";

const AddPath: React.FC = () => {
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const catalogUrl = `/v2/corporate/${corporateId}/detail/catalog`;

  const { submit: submitPath } = useSubmitPathV2({
    corporateId,
  });
  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: "path",
  });
  const { promoteDraft } = usePromoteDraft({ corporateId });

  const [successOpen, setSuccessOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [promoteError, setPromoteError] = useState<DraftErrorInfo | null>(null);

  const hasDraftChildren = (data: PathWizardData) =>
    data.selected_los.some((lo) => lo.entity?.is_draft);

  const handleComplete = async (data: PathWizardData) => {
    setIsPublishing(true);
    try {
      if (hasDraftChildren(data)) {
        const pathDraft = await saveDraft(data);
        try {
          await promoteDraft(pathDraft.id);
        } catch (err) {
          setPromoteError(extractPromoteError(err));
          throw err;
        }
      } else {
        await submitPath(data);
      }
      setSuccessOpen(true);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveDraft = async (data: PathWizardData) => {
    try {
      await saveDraft(data);
      navigate(`${catalogUrl}?tab=all`);
    } catch (error) {
      console.error("[path-wizard] saveDraft failed", error);
      throw error;
    }
  };

  return (
    <>
      <PathWizard
        initialData={EMPTY_PATH_DATA}
        onComplete={handleComplete}
        onSaveDraft={handleSaveDraft}
        isSavingDraft={isSavingDraft}
        isSubmitting={isPublishing}
      />
      <LOOutcomeModalCst
        open={successOpen}
        entityType="path"
        mode="add"
        onDismiss={() => navigate(catalogUrl)}
      />
      <DraftPromoteErrorDialog
        error={promoteError}
        onClose={() => setPromoteError(null)}
      />
    </>
  );
};

export default AddPath;
